import { uploadResponseSchema, type UploadResult } from '@competa/shared';
import { apiRequest } from '../../api/client';

/** Anzahl der Dateien je Upload-Anfrage (hält die Anfragen klein und den Fortschritt sichtbar). */
export const UPLOAD_BATCH_SIZE = 4;

/** Eine zum Hochladen vorgemerkte Datei mit ihrer Kategorie. */
export interface StagedFile {
  file: File;
  category: string;
}

/**
 * Lädt Dateien in Paketen hoch (nacheinander). Fehler eines ganzen Pakets (z. B. Netzwerk) werden als
 * Fehlerergebnis je Datei gemeldet, die übrigen Pakete laufen weiter.
 * @param url Pfad des Upload-Endpunkts relativ zur API-Basis (`/images` oder `/competitions/:id/images`).
 * @param files Vorgemerkte Dateien.
 * @param onProgress Wird nach jedem Paket mit den bisherigen Ergebnissen aufgerufen.
 * @returns Ergebnis je Datei in der Reihenfolge der Eingabe.
 */
export async function uploadInBatches(
  url: string,
  files: StagedFile[],
  onProgress?: (results: UploadResult[]) => void,
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];
  for (let start = 0; start < files.length; start += UPLOAD_BATCH_SIZE) {
    const batch = files.slice(start, start + UPLOAD_BATCH_SIZE);
    const form = new FormData();
    for (const staged of batch) {
      form.append('meta', JSON.stringify({ category: staged.category || null }));
      form.append('file', staged.file, staged.file.name);
    }
    try {
      const response = await apiRequest('POST', url, { form, schema: uploadResponseSchema });
      results.push(...response.results);
    } catch (error) {
      const code = (error as { code?: string }).code ?? 'unknown';
      for (const staged of batch) {
        results.push({
          filename: staged.file.name,
          status: 'error',
          imageId: null,
          title: null,
          code,
          submissionId: null,
        });
      }
    }
    onProgress?.([...results]);
  }
  return results;
}
